import { postChatTextAndGetIntents } from '~/api/chat/ai-models';
import { stripOptions, isChatbotCommand } from '~/utils/message-utils';

export const LS_KEYS = {
  tags: 'tags',
};

export const INITIAL_STATE = {
  list: [],
  chatbotIntents: [],
  isFeedbackRequested: false,
  isFeedbackGiven: false,
  isFeedbackTextRequested: false,
  isFeedbackTextGiven: false,
  isFeedbackSkipped: true,
  tags: [], // stores the tags for livechat purpose. Acha chatbot and CS Agent session.
};

export const state = () => ({ ...INITIAL_STATE });

export const getters = {
  getList(state) {
    return state.list;
  },
  getLastMessage(state) {
    if (state.list.length) return state.list[state.list.length - 1];
    return {};
  },
  chatbotIntents(state) {
    return state.chatbotIntents;
  },
  isFeedbackGiven(state) {
    return state.isFeedbackGiven;
  },
  isFeedbackRequested(state) {
    return state.isFeedbackRequested;
  },
  isFeedbackTextRequested(state) {
    return state.isFeedbackTextRequested;
  },
  isFeedbackTextGiven(state) {
    return state.isFeedbackTextGiven;
  },
  isFeedbackSkipped(state) {
    return state.isFeedbackSkipped;
  },

  /**
   * @param {INITIAL_STATE} state
   * @returns {string[]}
   */
  getTags(state) {
    if (process.browser) {
      const itemFromLS = localStorage.getItem(LS_KEYS.tags);
      const returnValue = state.tags.length > 0 ? state.tags : JSON.parse(itemFromLS);
      return returnValue || [];
    }
    return state.tags;
  },
};

export const mutations = {
  addMessage(state, message) {
    state.list = [...state.list, { id: state.list.length + 1, ...message }];
  },
  setChatbotIntents(state, payload) {
    state.chatbotIntents = payload;
  },
  setFeedbackRequestState(state, payload) {
    state.isFeedbackRequested = payload;
  },
  setFeedbackGivenState(state, payload) {
    state.isFeedbackGiven = payload;
  },
  setFeedbackTextRequested(state, payload) {
    state.isFeedbackTextRequested = payload;
  },
  setFeedbackTextGiven(state, payload) {
    state.isFeedbackTextGiven = payload;
  },
  setFeedbackSkippedState(state, payload) {
    state.isFeedbackSkipped = payload;
  },
  clearList(state) {
    state.list = [];
  },

  /**
   * @param {INITIAL_STATE} state
   * @param {string[]} payload
   */
  addTags(state, payload) {
    // Filter the tags accordingly, making sure there are no duplicated tags stored into the state and localStorage
    const existingTags = [...state.tags];
    const tagsToAdd = payload.filter(tag => !existingTags.includes(tag));

    const newTagList = [...existingTags, ...tagsToAdd];
    state.tags = [...newTagList];
    localStorage.setItem(LS_KEYS.tags, JSON.stringify(newTagList));
  },

  /**
   * @param {INITIAL_STATE} state
   * @param {string[]} payload
   */
  removeTags(state, payload) {
    const filteredTags = state.tags.filter(tag => !payload.includes(tag));
    state.tags = [...filteredTags];

    if (filteredTags.length === 0) {
      localStorage.removeItem(LS_KEYS.tags);
      return;
    }

    localStorage.setItem(LS_KEYS.tags, JSON.stringify(filteredTags));
  },
};

export const actions = {
  sendMessage({ commit }, data) {
    let messageObj = stripOptions(data);
    messageObj = {
      ...messageObj,
      msg: messageObj.msg ? messageObj.msg : '',
    };
    const isCommand = isChatbotCommand(data);

    commit('addMessage', {
      msg: messageObj.msg || '',
      imgUrl: messageObj.imgUrl,
      imgName: messageObj.imgName,
      imgSize: messageObj.imgSize,
      timestamp: messageObj.timestamp,
      nick: messageObj.nick,
      displayName: messageObj.display_name,
      options: messageObj.options || [],
      isChatbotCommand: isCommand,
      type: messageObj.type,
    });
  },
  setFeedbackRequestState({ commit }, payload) {
    commit('setFeedbackRequestState', payload);
  },
  setFeedbackGivenState({ commit }, payload) {
    commit('setFeedbackGivenState', payload);
  },
  setFeedbackTextGiven({ commit }, payload) {
    commit('setFeedbackTextGiven', payload);
  },
  setFeedbackTextRequested({ commit }, payload) {
    commit('setFeedbackTextRequested', payload);
  },
  setFeedbackSkippedState({ commit }, payload) {
    commit('setFeedbackSkippedState', payload);
  },

  /**
   * @param {object} context
   * @param {object} payload
   * @param {string[]} payload.tags
   */
  addTags({ commit }, { tags = [] }) {
    commit('addTags', tags);
  },

  /**
   * @param {object} context
   * @param {object} payload
   * @param {string[]} payload.tags
   */
  removeTags({ commit }, { tags = [] }) {
    commit('removeTags', tags);
  },

  /**
   * @param {object} context
   */
  removeAllTags({ commit, getters }) {
    commit('removeTags', getters.getTags);
  },

  /**
   * @param {object} context
   * @param {object[]} payload
   */
  async getChatbotIntent({ commit }, payload = []) {
    try {
      const response = await postChatTextAndGetIntents(this.$axios, payload);
      if (response.meta.http_status === 200) commit('setChatbotIntents', response.data);
      return response;
    } catch (error) {
      // handle error
      console.error('[ERROR FETCH CHATBOT INTENT]', error);
    }
  },
};
